import axios from 'axios'
import type {
  DocumentCategory,
  DocumentScope,
  DocumentType,
  DocumentTypeWritePayload,
  FetchDocumentTypesParams,
} from '../types/document.types'
import { BASE_URL } from '@/environment/environment'

const API_BASE = `${BASE_URL}/api/documents`
const TYPES_API_BASE = `${BASE_URL}/api/documents/type`

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

function normalizeTags(tags: unknown): string[] {
  if (Array.isArray(tags)) {
    return tags.map(tag => String(tag).trim()).filter(Boolean)
  }

  if (typeof tags === 'string' && tags.trim()) {
    try {
      const parsed = JSON.parse(tags) as unknown
      if (Array.isArray(parsed)) {
        return parsed.map(tag => String(tag).trim()).filter(Boolean)
      }
    } catch {
      return tags.split(',').map(tag => tag.trim()).filter(Boolean)
    }
  }

  return []
}

function normalizeDocumentType(item: DocumentType): DocumentType {
  return {
    ...item,
    tags: normalizeTags(item.tags),
  }
}

export class ReferencesApi {
  static async fetchTypes(params?: FetchDocumentTypesParams): Promise<DocumentType[]> {
    const { data } = await axios.get<PaginatedResponse<DocumentType> | DocumentType[]>(`${TYPES_API_BASE}/`, {
      params,
    })

    const results = Array.isArray(data) ? data : (data.results || [])

    return results.map(normalizeDocumentType)
  }

  static async createType(payload: DocumentTypeWritePayload): Promise<DocumentType> {
    const { data } = await axios.post<DocumentType>(`${TYPES_API_BASE}/`, payload)

    return normalizeDocumentType(data)
  }

  static async updateType(id: number, payload: Partial<DocumentTypeWritePayload>): Promise<DocumentType> {
    const { data } = await axios.patch<DocumentType>(`${TYPES_API_BASE}/${id}/`, payload)

    return normalizeDocumentType(data)
  }

  static async deleteType(id: number): Promise<void> {
    await axios.delete(`${TYPES_API_BASE}/${id}/`)
  }

  static async fetchScopes(): Promise<DocumentScope[]> {
    const { data } = await axios.get<PaginatedResponse<DocumentScope>>(`${API_BASE}/scope/`)

    return data.results || []
  }

  static async fetchCategories(): Promise<DocumentCategory[]> {
    const { data } = await axios.get<PaginatedResponse<DocumentCategory>>(`${API_BASE}/category/`)

    return data.results || []
  }
}
